import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeSanitize from 'rehype-sanitize';
import { decodeEntities } from '../utils/postContent';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * 게시물 마크다운 렌더러 — OpenEvidence식 잉크 미니멀 스타일.
 *
 * - 저장 시 이스케이프된 엔티티(&gt; 등)를 먼저 되돌린다(utils/postContent 참고).
 * - remark-breaks: 작성창에서 친 줄바꿈을 그대로 살린다(한 줄 개행이 문단으로 합쳐지지 않게).
 * - raw HTML은 렌더링하지 않는다(rehype-raw 미사용 + rehype-sanitize).
 * - react-markdown v10은 code에 `inline` 속성을 주지 않는다. 블록 코드는 <pre>에서 스타일을 잡고,
 *   <code>는 인라인 스타일로 둔 뒤 pre 안에서만 덮어쓴다.
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  return (
    <div className={`markdown-content text-gray-800 ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: ({ node, children, ...props }) => (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900 break-words"
              onClick={(e) => e.stopPropagation()}
            >
              {children}
            </a>
          ),
          h1: ({ node, children, ...props }) => (
            <h1 {...props} className="text-xl font-bold mt-6 mb-3 text-gray-900">{children}</h1>
          ),
          h2: ({ node, children, ...props }) => (
            <h2 {...props} className="text-lg font-bold mt-6 mb-2 text-gray-900">{children}</h2>
          ),
          h3: ({ node, children, ...props }) => (
            <h3 {...props} className="text-base font-semibold mt-5 mb-2 text-gray-900">{children}</h3>
          ),
          h4: ({ node, children, ...props }) => (
            <h4 {...props} className="text-sm font-semibold mt-4 mb-1 text-gray-900">{children}</h4>
          ),
          p: ({ node, ...props }) => <p {...props} className="mb-3 leading-relaxed" />,
          ul: ({ node, ...props }) => <ul {...props} className="list-disc pl-5 mb-3 space-y-1 marker:text-gray-400" />,
          ol: ({ node, ...props }) => <ol {...props} className="list-decimal pl-5 mb-3 space-y-1 marker:text-gray-500" />,
          li: ({ node, ...props }) => <li {...props} className="leading-relaxed pl-0.5" />,
          blockquote: ({ node, ...props }) => (
            <blockquote {...props} className="border-l-2 border-gray-900 pl-4 my-4 text-gray-600 [&>p]:mb-1" />
          ),
          pre: ({ node, ...props }) => (
            <pre
              {...props}
              className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 overflow-x-auto text-sm [&>code]:bg-transparent [&>code]:p-0 [&>code]:text-gray-800"
            />
          ),
          code: ({ node, ...props }: any) => (
            <code {...props} className="bg-gray-100 text-gray-900 px-1.5 py-0.5 rounded text-[0.875em] font-mono" />
          ),
          hr: ({ node, ...props }) => <hr {...props} className="my-6 border-gray-200" />,
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto mb-3">
              <table {...props} className="min-w-full border-collapse text-sm" />
            </div>
          ),
          thead: ({ node, ...props }) => <thead {...props} className="border-b border-gray-900" />,
          th: ({ node, ...props }) => <th {...props} className="px-3 py-2 text-left font-semibold text-gray-900" />,
          td: ({ node, ...props }) => <td {...props} className="px-3 py-2 border-b border-gray-100" />,
          strong: ({ node, ...props }) => <strong {...props} className="font-semibold text-gray-900" />,
          em: ({ node, ...props }) => <em {...props} className="italic" />,
        }}
      >
        {decodeEntities(content)}
      </ReactMarkdown>
    </div>
  );
};

/**
 * AI 상담 결과 글인지 감지 (하위 호환용 — 렌더링 분기는 looksLikeMarkdown으로 대체됨).
 */
export const isAIGeneratedContent = (content: string): boolean => {
  if (!content) return false;
  return [/\*\*질문:\*\*/, /\*\*AI 분석 결과:\*\*/, /\*\*AI.*결과:\*\*/i].some((p) => p.test(content));
};

export default MarkdownRenderer;
